<template>
  <div class="mobile-reports">
    <div class="mb-5">
      <div class="overline primary--text font-weight-bold">REPORTS</div>
      <h1 class="text-h4 font-weight-bold mb-1">Reports</h1>
      <p class="admin-muted mb-0">Choose a location, then open an available report.</p>
    </div>

    <v-card outlined class="admin-panel rounded-xl mb-5">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon color="primary" class="mr-3">mdi-map-marker-outline</v-icon>
        Select location
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-select
          v-model="selectedLocationId"
          :items="locations"
          item-text="name"
          item-value="id"
          :loading="loading"
          :disabled="loading"
          outlined
          hide-details
          label="Location"
          prepend-inner-icon="mdi-store-outline"
          no-data-text="No locations are available"
        />
        <v-alert v-if="error" type="error" text class="mt-4 mb-0" role="alert">
          {{ error }}
          <v-btn text color="error" min-height="44" @click="loadLocations">Try again</v-btn>
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card outlined class="admin-panel rounded-xl">
      <v-card-title class="text-subtitle-1 font-weight-bold">Available reports</v-card-title>
      <v-divider />
      <v-list class="pa-0" color="transparent">
        <v-list-item
          :disabled="!selectedLocationId"
          class="mobile-report-item"
          min-height="72"
          @click="openSerializedInventory"
        >
          <v-list-item-avatar color="primary"><v-icon dark>mdi-barcode-scan</v-icon></v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-bold">Serialized Inventory</v-list-item-title>
            <v-list-item-subtitle>
              {{ selectedLocationId ? "Browse available serial numbers by category and product" : "Select a location to open this report" }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
        </v-list-item>
        <v-divider />
        <v-list-item
          :disabled="!selectedLocationId"
          class="mobile-report-item"
          min-height="72"
          @click="openProfitAndLossDialog"
        >
          <v-list-item-avatar color="primary"><v-icon dark>mdi-chart-box-outline</v-icon></v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-bold">Profit and Loss</v-list-item-title>
            <v-list-item-subtitle>
              {{ selectedLocationId ? "Review income, costs, expenses, and receipts" : "Select a location to open this report" }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="dateDialog" v-drag-dialog max-width="520" content-class="mobile-report-dialog">
      <v-card class="rounded-xl">
        <CardToolbarDialog title="Profit and Loss" title-id="mobile-profit-loss-title" icon="mdi-chart-box-outline" @cancel="dateDialog = false" />
        <v-card-text class="mobile-report-dialog__body pa-5">
          <p class="admin-muted">Choose the period to include in the report.</p>
          <DateRangeSelector v-model="dateRange" default-range="monthToDate" />
          <v-alert v-if="dateError" type="error" text class="mt-4 mb-0" role="alert">{{ dateError }}</v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="mobile-report-dialog__actions pa-4">
          <v-spacer />
          <v-btn text min-height="44" @click="dateDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed min-height="44" @click="openProfitAndLoss">View report</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import locationService from "@/modules/Location/service";
import CardToolbarDialog from "@/components/Controls/Card-Toolbar-Dialog.vue";
import DateRangeSelector from "@/components/DateRangeSelector.vue";

export default {
  name: "MobileReports",
  components: { CardToolbarDialog, DateRangeSelector },
  data: () => ({
    loading: false,
    error: "",
    locations: [],
    selectedLocationId: null,
    dateDialog: false,
    dateRange: {},
    dateError: "",
  }),
  created() {
    this.loadLocations();
  },
  methods: {
    async loadLocations() {
      this.loading = true;
      this.error = "";
      try {
        const response = await locationService.getAll({ lite: true, limit: 500 });
        this.locations = response.data || [];
        if (this.locations.length && !this.locations.some((location) => location.id === this.selectedLocationId)) {
          this.selectedLocationId = this.locations[0].id;
        }
      } catch (error) {
        this.locations = [];
        this.error = "Locations could not be loaded. Check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
    openSerializedInventory() {
      if (!this.selectedLocationId) return;
      this.$router.push({
        name: "serialized-inventory",
        query: { LocationId: this.selectedLocationId },
      }).catch(() => {});
    },
    openProfitAndLossDialog() {
      if (!this.selectedLocationId) return;
      this.dateError = "";
      this.dateDialog = true;
    },
    openProfitAndLoss() {
      const start = this.$moment(this.dateRange.start);
      const end = this.$moment(this.dateRange.end);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
        this.dateError = "Choose a valid date range.";
        return;
      }
      this.dateDialog = false;
      this.$router.push({
        name: "mobile-profit-and-loss",
        query: {
          LocationId: this.selectedLocationId,
          start: start.startOf("day").toISOString(),
          end: end.endOf("day").toISOString(),
        },
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.mobile-reports { margin: 0 auto; max-width: 760px; min-height: calc(100vh - 120px); }
.mobile-report-item { min-height: 72px; }
.mobile-report-dialog { overflow: hidden; }
.mobile-report-dialog__body { max-height: calc(100vh - 190px); overflow-y: auto; }
.mobile-report-dialog__actions { flex: 0 0 auto; }
</style>
